import { useMemo } from 'react';
import { Eye, Info } from '@phosphor-icons/react';
import { Flex, Popover, Table, Typography } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { LocationFeedHook } from '@hooks/location-feed';
import { COLOR } from '@styles/theme';
import { CommonUtility, DateFormat, DateUtility } from '@utility/index';

const { Text } = Typography;

type LocationFeedRecord = {
  _id?: string;
  createdAt?: string;
  status?: string;
  downloadUrl?: string;
  results?: {
    messages?: {
      summary?: Record<string, unknown>;
    };
  };
};

function formatDynamicObject(obj: Record<string, unknown>) {
  return Object.entries(obj)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
}

type LocationFeedListProps = {
  warehouseId: string;
};

export function LocationFeedList({ warehouseId }: LocationFeedListProps) {
  const {
    filter: tableFilter,
    filterChanged,
    loading: feedsLoading,
    data: feedList,
    totalData,
    pageChanged,
  } = LocationFeedHook(warehouseId);

  const columns: ColumnsType<LocationFeedRecord> = useMemo(
    () => [
      {
        title: 'Time',
        key: 'start_time',
        width: '38%',
        render: (_, record) =>
          record.createdAt
            ? DateUtility.formatDate(record.createdAt, DateFormat.dateTime)
            : '—',
      },
      {
        title: 'Status',
        key: 'status',
        width: '32%',
        render: (_, record) => {
          const summary = record.results?.messages?.summary;
          const hasSummary = CommonUtility.isValidObject(summary);

          return (
            <Flex align="center" gap={6}>
              <Text style={{ textTransform: 'capitalize' }}>{record.status ?? '—'}</Text>
              {hasSummary && summary ? (
                <Popover content={formatDynamicObject(summary)} trigger="click">
                  <Info
                    size={16}
                    color={COLOR.textSecondary}
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                  />
                </Popover>
              ) : null}
            </Flex>
          );
        },
      },
      {
        title: 'File',
        key: 'file',
        width: '30%',
        render: (_, record) =>
          record.downloadUrl ? (
            <a
              href={record.downloadUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <Eye size={18} />
              <span>View File</span>
            </a>
          ) : (
            '—'
          ),
      },
    ],
    [],
  );

  const pagination: TablePaginationConfig = {
    current: tableFilter.page,
    pageSize: tableFilter.limit,
    total: totalData,
    showSizeChanger: true,
    pageSizeOptions: [5, 10, 20],
    showTotal: total => `${total} records`,
    size: 'small',
    onChange: (page, pageSize) => {
      if (pageSize !== tableFilter.limit) {
        filterChanged('limit', pageSize);
      } else {
        pageChanged(page);
      }
    },
  };

  return (
    <Table<LocationFeedRecord>
      rowKey={(record, index) =>
        record._id ?? (record.createdAt ? `${record.createdAt}-${index}` : String(index))
      }
      columns={columns}
      dataSource={feedList as LocationFeedRecord[]}
      loading={feedsLoading}
      size="small"
      pagination={pagination}
      scroll={{ y: '40vh' }}
      tableLayout="fixed"
      style={{
        border: `1px solid ${COLOR.border}`,
        borderRadius: 6,
        overflow: 'hidden',
      }}
    />
  );
}
